import React, { useState } from "react";
import { api, formatApiError, fmtINR, API } from "../lib/apiClient";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import { DownloadSimple, ChartLineUp, FileCsv, FileXls } from "@phosphor-icons/react";

// Module-level constants — table headers don't depend on state, so hoist them out
// to avoid creating new array references on every render (minor perf win).
const HEADERS_OUTWARD = ["Nature of Supplies", "Taxable Value", "IGST", "CGST", "SGST", "Cess"];
const HEADERS_INTERSTATE = ["Place of Supply (State Code)", "Taxable Value", "IGST"];
const HEADERS_TAX = ["Details", "IGST", "CGST", "SGST", "Cess"];
const HEADERS_PAYABLE = ["Description", "IGST", "CGST", "SGST", "Cess"];

function firstDayOfMonth(d = new Date()) { return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }
function lastDayOfMonth(d = new Date()) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10); }

export default function Gstr3bReportPage() {
  const [from, setFrom] = useState(firstDayOfMonth());
  const [to, setTo] = useState(lastDayOfMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/gstr3b", { params: { from, to } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const downloadJson = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `gstr3b-${from}-to-${to}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("GSTR-3B JSON downloaded");
  };

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API}/reports/gstr3b.csv?from=${from}&to=${to}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `gstr3b-${from}-to-${to}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("GSTR-3B CSV downloaded");
    } catch { toast.error("Download failed"); }
  };

  const downloadXlsx = async () => {
    try {
      const res = await fetch(`${API}/reports/gstr3b.xlsx?from=${from}&to=${to}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `gstr3b-${from}-to-${to}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("GSTR-3B Excel downloaded");
    } catch { toast.error("Download failed"); }
  };

  return (
    <div data-testid="gstr3b-page">
      <PageHeader
        title="GSTR-3B Report"
        subtitle="Monthly summary return — outward supplies, ITC and tax payable. Download JSON or CSV for filing & reconciliation."
      />

      <div className="p-8 max-w-5xl space-y-6">
        <div className="card-base p-5 flex items-end gap-4 flex-wrap">
          <div>
            <div className="label-overline block mb-1.5">From</div>
            <input type="date" className="field" value={from} onChange={e => setFrom(e.target.value)} data-testid="g3b-from" />
          </div>
          <div>
            <div className="label-overline block mb-1.5">To</div>
            <input type="date" className="field" value={to} onChange={e => setTo(e.target.value)} data-testid="g3b-to" />
          </div>
          <button onClick={generate} disabled={loading} className="btn-primary inline-flex items-center gap-1.5" data-testid="g3b-generate">
            <ChartLineUp size={14} weight="bold" /> {loading ? "Generating…" : "Generate Report"}
          </button>
          {data && <>
            <button onClick={downloadJson} className="btn-secondary inline-flex items-center gap-1.5" data-testid="g3b-download-json"><DownloadSimple size={14} weight="bold" /> JSON</button>
            <button onClick={downloadCsv} className="btn-secondary inline-flex items-center gap-1.5" data-testid="g3b-download-csv"><FileCsv size={14} weight="bold" /> CSV</button>
            <button onClick={downloadXlsx} className="btn-secondary inline-flex items-center gap-1.5" data-testid="g3b-download-xlsx"><FileXls size={14} weight="bold" /> Excel</button>
          </>}
        </div>

        {data && (
          <>
            {/* Header strip */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <KpiLite label="GSTIN" value={data.gstin || "—"} mono />
              <KpiLite label="Return Period" value={data.ret_period} mono />
              <KpiLite label="From → To" value={`${data.period.from} → ${data.period.to}`} mono />
              <KpiLite label="Documents" value={`${data.doc_counts.invoices + data.doc_counts.repair_invoices} sales · ${data.doc_counts.credit_notes + data.doc_counts.debit_notes} CN/DN · ${data.doc_counts.purchase_orders} PO`} />
            </div>

            {/* 3.1 outward */}
            <Section title="3.1 Outward supplies and inward supplies liable to reverse charge">
              <Table headers={HEADERS_OUTWARD}>
                <Row label="(a) Outward taxable supplies (other than zero rated, nil rated and exempted)" v={data.sup_details.osup_det} />
                <Row label="(b) Outward taxable supplies (zero rated)" v={data.sup_details.osup_zero} noCs />
                <Row label="(c) Other outward supplies (nil rated, exempted)" v={data.sup_details.osup_nil_exmp} onlyTx />
                <Row label="(d) Inward supplies (liable to reverse charge)" v={data.sup_details.isup_rev} />
                <Row label="(e) Non-GST outward supplies" v={data.sup_details.osup_nongst} onlyTx />
              </Table>
            </Section>

            {/* 3.2 inter-state */}
            <Section title="3.2 Of the supplies in 3.1(a), inter-state to URP / Composition / UIN">
              {data.inter_sup.unreg_details.length === 0 ? (
                <div className="text-xs text-ink-muted">No inter-state outward supplies to unregistered persons in this period.</div>
              ) : (
                <Table headers={HEADERS_INTERSTATE}>
                  {data.inter_sup.unreg_details.map(r => (
                    <tr key={r.pos}>
                      <td className="font-mono">{r.pos}</td>
                      <td className="text-right tabular">{fmtINR(r.txval)}</td>
                      <td className="text-right tabular">{fmtINR(r.iamt)}</td>
                    </tr>
                  ))}
                </Table>
              )}
            </Section>

            {/* 4 ITC */}
            <Section title="4. Eligible ITC">
              <Table headers={HEADERS_TAX}>
                {data.itc_elg.itc_avl.map(r => (
                  <tr key={`avl-${r.ty}`}>
                    <td>(A) ITC Available — {r.ty}</td>
                    <td className="text-right tabular">{fmtINR(r.iamt)}</td>
                    <td className="text-right tabular">{fmtINR(r.camt)}</td>
                    <td className="text-right tabular">{fmtINR(r.samt)}</td>
                    <td className="text-right tabular">{fmtINR(r.csamt)}</td>
                  </tr>
                ))}
                {data.itc_elg.itc_rev.map(r => (
                  <tr key={`rev-${r.ty}`}>
                    <td>(B) ITC Reversed — {r.ty}</td>
                    <td className="text-right tabular">{fmtINR(r.iamt)}</td>
                    <td className="text-right tabular">{fmtINR(r.camt)}</td>
                    <td className="text-right tabular">{fmtINR(r.samt)}</td>
                    <td className="text-right tabular">{fmtINR(r.csamt)}</td>
                  </tr>
                ))}
                <tr className="bg-brand-secondary/40 font-semibold">
                  <td>(C) Net ITC Available (A − B)</td>
                  <td className="text-right tabular">{fmtINR(data.itc_elg.itc_net.iamt)}</td>
                  <td className="text-right tabular">{fmtINR(data.itc_elg.itc_net.camt)}</td>
                  <td className="text-right tabular">{fmtINR(data.itc_elg.itc_net.samt)}</td>
                  <td className="text-right tabular">{fmtINR(data.itc_elg.itc_net.csamt)}</td>
                </tr>
              </Table>
              <div className="text-[11px] text-ink-muted mt-2">
                ITC values derived from Purchase Orders in the period. Add manual entries for imports / RCM / ISD before final filing.
              </div>
            </Section>

            {/* 6.1 tax payable */}
            <Section title="6.1 Payment of Tax (Output liability)">
              <Table headers={HEADERS_PAYABLE}>
                <tr className="font-semibold">
                  <td>Tax payable from outward supplies</td>
                  <td className="text-right tabular">{fmtINR(data.tax_payable.iamt)}</td>
                  <td className="text-right tabular">{fmtINR(data.tax_payable.camt)}</td>
                  <td className="text-right tabular">{fmtINR(data.tax_payable.samt)}</td>
                  <td className="text-right tabular">{fmtINR(data.tax_payable.csamt)}</td>
                </tr>
              </Table>
            </Section>

            <div className="card-base p-4 bg-surface-muted/50 text-[11px] text-ink-muted">
              <strong>Note:</strong> This is a working summary based on documents in the system. Validate and adjust before submitting on the GSTN portal.
            </div>
          </>
        )}
      </div>

      <FieldStyle />
    </div>
  );
}

function KpiLite({ label, value, mono }) {
  return (
    <div className="card-base p-4">
      <div className="label-overline mb-1">{label}</div>
      <div className={`font-semibold text-ink ${mono ? "font-mono text-sm" : "text-sm tabular"}`}>{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="card-base p-5">
      <div className="font-display font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

function Table({ headers, children }) {
  return (
    <div className="table-wrapper border-0 rounded-md">
      <table className="table-base">
        <thead><tr>{headers.map((h, i) => <th key={h} className={i === 0 ? "" : "text-right"}>{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Row({ label, v, onlyTx, noCs }) {
  return (
    <tr>
      <td>{label}</td>
      <td className="text-right tabular">{fmtINR(v.txval || 0)}</td>
      {onlyTx ? <><td className="text-right text-ink-muted">—</td><td className="text-right text-ink-muted">—</td><td className="text-right text-ink-muted">—</td><td className="text-right text-ink-muted">—</td></> : (
        <>
          <td className="text-right tabular">{fmtINR(v.iamt || 0)}</td>
          <td className="text-right tabular">{noCs ? "—" : fmtINR(v.camt || 0)}</td>
          <td className="text-right tabular">{noCs ? "—" : fmtINR(v.samt || 0)}</td>
          <td className="text-right tabular">{fmtINR(v.csamt || 0)}</td>
        </>
      )}
    </tr>
  );
}
